import { utils } from '@clickcease/cc-client-components-lib'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { HelpCenterRedirect } from '../../../../../../HelpCenterRedirect/HelpCenterRedirect'
import { useGetGoogleAccounts } from '../../../../../api/getGoogleAdAccounts'
import { useGetPlatforms } from '../../../../../api/getPlatforms'
import { useSetSelectedGoogleAccounts } from '../../../../../api/setSelectedGoogleAdAccounts'
import { PaginatedCheckboxModal } from '../../../../common/PaginatedCheckboxModal/PaginatedCheckboxModal'
import { GenericPlatformEntity, PLATFORMS, PLATFORM_MODALS } from '../../../../../models'
import usePlatformsModalsStore from '../../../../../stores/platformsModals.store'
import { useCheckboxPaginationWithInitialIDs } from '../../../../../../../hooks/pagination/useCheckboxPaginationWithInitialIDs'
import {
  DEFAULT_PAGINATION_PARAMS,
  PaginationMetadata,
  PaginationParams,
} from '../../../../../../../types/pagination'

import { TOASTS_TYPES } from 'Stores/app'
import { ZENDESK_LINKS } from '../../../../../../../utils/zendeskLinks'

interface SelectAccountIdsProps {
  connectionId: number
  onBack: () => void
  onClose?: () => void
  isEdit?: boolean
}

export const NewSelectAccountIds = ({
  connectionId,
  onBack,
  onClose,
  isEdit,
}: SelectAccountIdsProps) => {
  const { t } = useTranslation('PMPlatforms')
  const { data: platformsData } = useGetPlatforms()
  const { mutate, isLoading } = useSetSelectedGoogleAccounts()
  const [paginationParams, setPaginationParams] =
    useState<PaginationParams>(DEFAULT_PAGINATION_PARAMS)

  const { data: accountsResponse, isFetching: accountsAreLoading } = useGetGoogleAccounts({
    connectionId,
    pagination: paginationParams,
  })

  const googleConnections = useMemo(() => {
    const googleDisplayIds: string[] = []
    platformsData?.forEach((platform) => {
      if (platform.platformType === 'Google') {
        googleDisplayIds.push(platform.displayId)
      }
    })
    return googleDisplayIds
  }, [platformsData])

  const accounts = useMemo(() => {
    if (accountsResponse instanceof Error || !accountsResponse) return []

    return accountsResponse.adAccounts.length < 2
      ? accountsResponse.adAccounts
      : accountsResponse.adAccounts.filter((item) => {
          return item.adAccountID && !googleConnections.includes(item.adAccountID)
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsResponse?.adAccounts, googleConnections])

  const getMetaDataParam = (param: keyof PaginationMetadata) =>
    utils.get(accountsResponse, `metadata.${param}`, 0)

  const {
    sort,
    page,
    search,
    pageCount,
    setPageCount,
    setSort,
    searchPage,
    onFirstPage,
    onLastPage,
    onNextPage,
    onPrevPage,
    handleSelectAll,
    selectedOptions,
    handleCheckboxClicked,
    paginationParams: hookPaginationParams,
    selectedText,
    isAllSelected,
  } = useCheckboxPaginationWithInitialIDs({
    entities: accounts as GenericPlatformEntity[],
    totalCount: getMetaDataParam('totalCount') as number,
    serverSelectedCount: getMetaDataParam('selected') as number,
    serverSelectedOptions: accountsResponse?.selected,
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPageCount(getMetaDataParam('pageCount')), [accountsResponse]) // update page count
  useEffect(() => setPaginationParams(hookPaginationParams), [hookPaginationParams]) // update pagination params

  const {
    openAudienceModal,
    closeModal,
    setOpenModal,
    setGoogleAdsIdUpdated,
    setSelectedConnectionId,
  } = usePlatformsModalsStore()

  // SET ACCOUNTS LOGIC
  const handleSubmit = () => {
    const isValueUpdated = selectedOptions.length !== accountsResponse?.metadata?.selected
    mutate(
      {
        connectionId,
        adAccountsIds: selectedOptions,
      },
      {
        onSuccess: (data) => {
          if (!(data instanceof Error)) {
            closeModal()
            onClose && onClose()
            setSelectedConnectionId(undefined)
            setGoogleAdsIdUpdated(!!isValueUpdated)
            // If changes been made, we need to show success message otherwise we open Audience Modal
            isValueUpdated || isEdit
              ? setOpenModal(PLATFORM_MODALS.successModal)
              : openAudienceModal(PLATFORMS.GOOGLE)()
          }
        },
      }
    )
  }

  const showNoAccountsMessage = () =>
    !accountsAreLoading && (!accounts || accounts.length === 0)
      ? {
          content: (
            <HelpCenterRedirect
              text={t('google-connection-wizard.no-addAccounts-message')}
              article={ZENDESK_LINKS.pixelGuardOverview}
            />
          ),
          type: TOASTS_TYPES.WARNING,
        }
      : undefined

  return (
    <PaginatedCheckboxModal
      title={t('google-connection-wizard.step-3-title')}
      searchPlaceholder={t('google-connection-wizard.google-id-search')}
      buttonText={t('google-connection-wizard.add-account')}
      entitiesName={t('google-connection-wizard.ad-account')}
      buttonLoading={isLoading}
      modalLoading={accountsAreLoading}
      messageBox={showNoAccountsMessage()}
      isBackButtonDisabled
      selectedText={selectedText}
      page={page}
      sort={sort}
      isAllSelected={isAllSelected}
      setSort={setSort}
      handleSubmit={handleSubmit}
      handleSelectAll={handleSelectAll}
      onFirstPage={onFirstPage}
      onLastPage={onLastPage}
      onNextPage={onNextPage}
      onPreviousPage={onPrevPage}
      onBack={onBack}
      filterBy={search}
      options={accounts}
      selected={selectedOptions}
      handleSelect={handleCheckboxClicked}
      onSearch={searchPage}
      pageCount={pageCount ?? 1}
      totalItems={
        accountsResponse?.metadata?.totalCount ? accountsResponse.metadata.totalCount : undefined
      }
    />
  )
}
